/**
 * Trades and free agents, during the season. A trade happens once the other player accepts it and room staff
 * approve it, as on Showdown.
 */
import { getAvailable, type DraftTrade } from '@ogreclan/league-core';
import type { PokemonData } from '@ogreclan/pokemon-core';
import { useState } from 'preact/hooks';
import type { LeagueProps } from '../pages/league';
import { Mon, nameOf } from '../pokemon';
import { ActionButton } from '../ui';
import { playerName, you as findYou } from './common';

function Mons({ ids, data }: { ids: string[], data: PokemonData | null }) {
	return <>{ids.map((id, i) => <span key={id}>{i ? ', ' : ''}<Mon data={data} id={id} /></span>)}</>;
}

function Picker({ ids, data, chosen, onChange }: { ids: string[], data: PokemonData | null, chosen: string[], onChange: (ids: string[]) => void }) {
	return <div class="stack" style="max-height:260px;overflow-y:auto">
		{ids.map(id => <label key={id} class="row" style="gap:6px">
			<input type="checkbox" checked={chosen.includes(id)} onChange={e => onChange((e.target as HTMLInputElement).checked ?
				[...chosen, id] : chosen.filter(c => c !== id))} />
			<Mon data={data} id={id} />
		</label>)}
		{!ids.length && <p class="empty">No Pokémon.</p>}
	</div>;
}

export function TradesTab({ view, data, act }: LeagueProps) {
	const { league } = view;
	const me = findYou(league, view.you)!;
	const [partnerId, setPartnerId] = useState('');
	const [give, setGive] = useState<string[]>([]);
	const [get, setGet] = useState<string[]>([]);
	const [drop, setDrop] = useState('');
	const [pickup, setPickup] = useState('');
	const partner = league.players.find(p => p.id === partnerId);
	const tradesLeft = league.settings.trades - me.tradesUsed;
	const freeAgentsLeft = league.settings.freeAgents - me.freeAgentsUsed;
	const mine = league.trades.filter(t => (t.from === me.id || t.to === me.id) && ['pending', 'agreed'].includes(t.status));
	const forStaff = view.role === 'staff' ? league.trades.filter(t => t.status === 'agreed') : [];

	const describe = (trade: DraftTrade) => {
		const incoming = trade.to === me.id;
		const other = playerName(league, incoming ? trade.from : trade.to);
		const yours = incoming ? trade.get : trade.give;
		const theirs = incoming ? trade.give : trade.get;
		if (trade.status === 'agreed') {
			return <>You and <strong>{other}</strong> agreed to swap your <Mons ids={yours} data={data} /> for their{' '}
				<Mons ids={theirs} data={data} />. <em>Waiting for room staff to approve it.</em></>;
		}
		return incoming ? <><strong>{other}</strong> offers their <Mons ids={theirs} data={data} /> for your <Mons ids={yours} data={data} />.</> :
			<>You offered <strong>{other}</strong> your <Mons ids={yours} data={data} /> for their <Mons ids={theirs} data={data} />.</>;
	};

	return <>
		<p>{tradesLeft} trade{tradesLeft === 1 ? '' : 's'} and {freeAgentsLeft} free agent pickup{freeAgentsLeft === 1 ? '' : 's'} left.</p>
		{forStaff.length > 0 && <div class="card">
			<h2 style="margin-top:0">Waiting for approval</h2>
			{forStaff.map(trade => <div key={trade.id} class="notice">
				{playerName(league, trade.from)} sends <Mons ids={trade.give} data={data} /> to {playerName(league, trade.to)} for{' '}
				<Mons ids={trade.get} data={data} />.{' '}
				<ActionButton class="primary small" action={() => act('trade', `approve ${trade.id}`)}>Approve</ActionButton>{' '}
				<ActionButton class="small danger" confirm="Reject this trade?" action={() => act('trade', `reject ${trade.id}`)}>Reject</ActionButton>
			</div>)}
		</div>}
		<div class="card">
			<h2 style="margin-top:0">Your trades</h2>
			{mine.map(trade => <div key={trade.id} class="notice">
				{describe(trade)}{' '}
				{trade.status === 'pending' && trade.to === me.id ? <>
					<ActionButton class="primary small" action={() => act('trade', `accept ${trade.id}`)}>Accept</ActionButton>{' '}
					<ActionButton class="small" action={() => act('trade', `decline ${trade.id}`)}>Decline</ActionButton>
				</> : <ActionButton class="small" action={() => act('trade', `cancel ${trade.id}`)}>{trade.status === 'agreed' ? 'Call it off' : 'Cancel the offer'}</ActionButton>}
			</div>)}
			{!mine.length && <p class="empty">No trades on the go.</p>}
			{tradesLeft > 0 && <>
				<h3>Offer a trade</h3>
				<p><select value={partnerId} onChange={e => {
					setPartnerId((e.target as HTMLSelectElement).value);
					setGet([]);
				}}>
					<option value="">Trade with…</option>
					{league.players.filter(p => p !== me).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
				</select></p>
				{partner && <div class="grid">
					<div><h3>You give</h3><Picker ids={me.roster} data={data} chosen={give} onChange={setGive} /></div>
					<div><h3>You get</h3><Picker ids={partner.roster} data={data} chosen={get} onChange={setGet} /></div>
				</div>}
				{partner && <ActionButton class="primary" disabled={!give.length || !get.length} action={async () => {
					if (await act('trade', `${partner.id} | ${give.join(', ')} | ${get.join(', ')}`)) {
						setGive([]);
						setGet([]);
					}
				}}>Offer {partner.name} this trade</ActionButton>}
			</>}
		</div>
		{freeAgentsLeft > 0 && <div class="card">
			<h2 style="margin-top:0">Free agents</h2>
			<p class="muted">Swap one of your Pokémon for one no one has drafted.</p>
			<form class="row" onSubmit={e => {
				e.preventDefault();
				void act('freeagent', `${drop} | ${pickup}`).then(ok => {
					if (ok) setPickup('');
				});
			}}>
				<select value={drop} onChange={e => setDrop((e.target as HTMLSelectElement).value)} required>
					<option value="">Drop…</option>
					{me.roster.map(id => <option key={id} value={id}>{nameOf(data, id)}</option>)}
				</select>
				<input list="free-agents" placeholder="Pick up…" value={pickup} onInput={e => setPickup((e.target as HTMLInputElement).value)} required />
				<datalist id="free-agents">{getAvailable(league).map(id => <option key={id} value={nameOf(data, id)} />)}</datalist>
				<button type="submit">Swap</button>
			</form>
		</div>}
		<div class="card">
			<h2 style="margin-top:0">Your trades and pickups so far</h2>
			<ul class="history">{league.transactions.filter(text => text.includes(me.name)).reverse().map((text, i) => <li key={i}>{text}</li>)}</ul>
			{!league.transactions.some(text => text.includes(me.name)) && <p class="empty">None yet.</p>}
		</div>
	</>;
}
